// src/pages/AssetStudioSettingsPage.jsx
// Asset Studio: org-level, single "Asset Upload Form" controller.
// This page now:
//  • Detects if the org already has a published Asset Upload Form (via org pointer)
//  • Falls back to detecting an existing **published** special form when pointer == none
//  • Falls back to detecting an existing **draft** special form when no published found
//  • (Optional) Self-heals the pointer when we find a published form via fallback and user has edit/publish perms
//  • Keeps logs verbose to match your existing debug style.
//
// Next steps (not in this patch):
//  • Wire “Publish” and “Delete/Reset” (we’ll add publish pointer + archive/reset flow).
//  • Add lastModified/publishedAt display and any warnings (e.g., license expiry).
//
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Alert,
  CircularProgress,
  Paper,               // ✅ match UserProfilePage layout wrapper
  Grid,                // ✅ for laying out HomeCard
  Skeleton,            // ✅ single skeleton to avoid flicker
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description"; // ✅ zero-state icon
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ensureAssetUploadFormForOrg } from "../utils/assetUploadForm";
import { db } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  updateDoc,
  serverTimestamp,
  where,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";

// ✅ show the AUF as a card just like on Home
import HomeCard from "../components/Home/HomeCard";
import { FormProvider } from "../contexts/FormContext";

// NEW: archived AUF fields table
import ArchivedAufFieldsTable from "../AssetManager/ArchivedAufFieldsTable";

import { safeSetItem } from "../utils/storageUtils";

const LOG_PREFIX = "[AssetStudio]";

// AUF Restore (pending) is org-scoped so it cannot bleed across organizations.
// We also validate aufId in the builder before applying.
const AUF_RESTORE_KEY = (orgId) => `org_${orgId}_aufRestorePending`;

const AssetStudioSettingsPage = () => {

  const navigate = useNavigate();
  const { hasPermission, organizationId, currentUser } = useAuth();

  // DAM-form permissions
  const canCreate  = hasPermission("createUploadAssetForm");
  const canEdit    = hasPermission("editUploadAssetForm");
  const canPublish = hasPermission("publishUploadAssetForm");
  // const canDelete  = hasPermission("deleteUploadAssetForm"); // not used on this screen now

  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  // Discovered forms
  const [publishedForm, setPublishedForm] = useState(null); // { id, status, orgAccess, title, ... } or null
  const [draftForm, setDraftForm] = useState(null);         // same shape or null (retained for future use if needed)
  const [errorMsg, setErrorMsg] = useState("");

  // NEW: archived AUF fields for the “active” AUF (published or draft)
  const [archivedFields, setArchivedFields] = useState([]);

  /**
   * Fetch org pointer + published fallback + draft fallback.
   * Priority:
   *   1) org.assetUploadFormId → fetch forms/{id} → expect status:'published'
   *   2) if no pointer → search for a PUBLISHED special form (legacy publish before pointer)
   *   3) if still nothing → search for a DRAFT special form
   *   4) (optional) self-heal: if we found PUBLISHED via fallback, set pointer
   */
  const refreshState = useCallback(async () => {
    if (!organizationId) {
      console.warn(
        `${LOG_PREFIX} Missing organizationId; cannot load pointers yet.`,
      );
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setErrorMsg("");

      // 1) Read org pointer (published)
      const orgRef = doc(db, "organizations", organizationId);
      const orgSnap = await getDoc(orgRef);

      let ptr = null;
      if (orgSnap.exists()) {
        ptr = orgSnap.data()?.assetUploadFormId || null;
        console.log(
          `${LOG_PREFIX} Org pointer assetUploadFormId:`,
          ptr || "(none)",
        );
      } else {
        console.warn(
          `${LOG_PREFIX} Organization doc not found for ${organizationId}`,
        );
      }

      // If pointer found, fetch published form
      let published = null;
      if (ptr) {
        const pubRef = doc(db, "forms", ptr);
        const pubSnap = await getDoc(pubRef);
        if (pubSnap.exists()) {
          const d = pubSnap.data();
          if (d?.isAssetUploadForm === true && d?.status === "published") {
            published = { id: pubSnap.id, ...d };
            console.log(
              `${LOG_PREFIX} Found PUBLISHED Asset Upload Form via pointer:`,
              published.id,
            );
          } else {
            console.warn(
              `${LOG_PREFIX} Pointer form is not published or not marked isAssetUploadForm.`,
              {
                id: pubSnap.id,
                status: d?.status,
                isAssetUploadForm: d?.isAssetUploadForm,
              },
            );
          }
        } else {
          console.warn(
            `${LOG_PREFIX} Pointer form ${ptr} does not exist.`,
          );
        }
      }

      // 2) If no published pointer form, try to find a PUBLISHED special form (legacy case)
      if (!published) {
        const formsRef = collection(db, "forms");
        const pq = query(
          formsRef,
          where("organizationId", "==", organizationId),
          where("isAssetUploadForm", "==", true),
          where("status", "==", "published"),
          limit(1),
        );
        const pSnap = await getDocs(pq);
        if (!pSnap.empty) {
          const first = pSnap.docs[0];
          published = { id: first.id, ...first.data() };
          console.log(
            `${LOG_PREFIX} Found PUBLISHED Asset Upload Form (fallback, no pointer):`,
            published.id,
          );

          // 2a) Optional self-heal: if we found published via fallback, set pointer for future speed
          //     Only try to write if user has edit or publish DAM permissions.
          if ((canEdit || canPublish) && organizationId) {
            try {
              await updateDoc(orgRef, {
                assetUploadFormId: published.id,
                assetUploadFormPublishedAt: serverTimestamp(),
              });
              console.log(
                `${LOG_PREFIX} Wrote missing pointer organizations/${organizationId}.assetUploadFormId = ${published.id}`,
              );
            } catch (e) {
              console.warn(
                `${LOG_PREFIX} Could not self-heal pointer:`,
                e,
              );
            }
          }
        } else {
          console.log(
            `${LOG_PREFIX} No published Asset Upload Form found via fallback.`,
          );
        }
      }

      // 3) If still nothing published, search for a DRAFT special form
      let draft = null;
      if (!published) {
        const formsRef = collection(db, "forms");
        const dq = query(
          formsRef,
          where("organizationId", "==", organizationId),
          where("isAssetUploadForm", "==", true),
          where("status", "==", "draft"),
          limit(1),
        );
        const dSnap = await getDocs(dq);
        if (!dSnap.empty) {
          const first = dSnap.docs[0];
          draft = { id: first.id, ...first.data() };
          console.log(
            `${LOG_PREFIX} Found DRAFT Asset Upload Form:`,
            draft.id,
          );
        } else {
          console.log(
            `${LOG_PREFIX} No draft Asset Upload Form found.`,
          );
        }
      }

      setPublishedForm(published);
      setDraftForm(draft);
    } catch (err) {
      console.error(
        `${LOG_PREFIX} Error while refreshing state:`,
        err,
      );
      setErrorMsg(
        "Unable to read current Asset Upload Form state. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [organizationId, canEdit, canPublish]);

  useEffect(() => {
    refreshState();
  }, [refreshState]);

  /* ─────────────────────────────────────────────────────────────
     Load archivedAufFields for the *active* AUF (published or draft)
     whenever it changes.
     ──────────────────────────────────────────────────────────── */
  useEffect(() => {
    const activeForm = publishedForm || draftForm;

    if (!activeForm) {
      setArchivedFields([]);
      return;
    }

    (async () => {
      try {
        const formRef = doc(db, "forms", activeForm.id);
        const snap = await getDoc(formRef);
        if (!snap.exists()) {
          setArchivedFields([]);
          return;
        }
        const data = snap.data() || {};
        const archived = Array.isArray(data.archivedAufFields)
          ? data.archivedAufFields
          : [];
        setArchivedFields(archived);
        console.log("[AssetStudio] archivedAufFields loaded:", {
          formId: activeForm.id,
          count: archived.length,
        });
      } catch (e) {
        console.warn(
          "[AssetStudio] Failed to load archivedAufFields (non-fatal):",
          e,
        );
        setArchivedFields([]);
      }
    })();
  }, [publishedForm, draftForm]);

  /**
   * Create (or reuse) the org's single Asset Upload Form, then navigate to builder.
   * Uses dedicated util (no FormContext here) to keep Settings decoupled.
   */
  const handleCreate = useCallback(async () => {
    if (!canCreate) {
      console.warn(
        `${LOG_PREFIX} Create clicked without permission (createUploadAssetForm).`,
      );
      return;
    }
    if (!organizationId || !currentUser?.uid) {
      console.error(
        `${LOG_PREFIX} Missing organizationId or currentUser; cannot create.`,
      );
      alert("Missing organization or user context.");
      return;
    }

    try {
      setBusy(true);
      console.log(
        `${LOG_PREFIX} Ensuring org-scoped Asset Upload Form exists…`,
        {
          organizationId,
          userId: currentUser.uid,
        },
      );

      const formId = await ensureAssetUploadFormForOrg({
        organizationId,
        userId: currentUser.uid,
      });

      // ─────────────────────────────────────────────────────────────
      // Mark the form as the org's Asset Upload Form (no mapping here).
      // Mapping is dynamic and must be saved by the builder when the
      // admin selects roles; see utils/firestore.saveDraftFormToFirestore.
      // ─────────────────────────────────────────────────────────────
      try {
        const formRef = doc(db, "forms", formId);
        await updateDoc(formRef, {
          isAssetUploadForm: true,
          lastModified: serverTimestamp(),
          lastModifiedBy: currentUser.uid,
        });
        console.log(
          `${LOG_PREFIX} Set isAssetUploadForm=true on forms/${formId}`,
        );
      } catch (seedErr) {
        console.warn(
          `${LOG_PREFIX} Could not set isAssetUploadForm marker:`,
          seedErr,
        );
      }

      console.log(
        `${LOG_PREFIX} Navigating to builder for Asset Upload Form:`,
        formId,
      );
      navigate(`/form-builder/${formId}/design`, {
        state: { createNewForm: true },
      });
    } catch (err) {
      console.error(
        `${LOG_PREFIX} Error creating/fetching Asset Upload Form:`,
        err,
      );
      alert(
        "Unable to create or access the Asset Upload Form. Please try again.",
      );
    } finally {
      setBusy(false);
      // Re-check state after a brief delay in case a new doc was just created
      setTimeout(refreshState, 400);
    }
  }, [canCreate, organizationId, currentUser, navigate, refreshState]);

  /* ─────────────────────────────────────────────────────────────
     React to archive from HomeCard (optimistic + refresh)
     – Optimistically hide the AUF card when onDelete fires.
     – Then call refreshState() to load the real current state.
     – Avoids stuck UI when pointer was cleared and form archived.
     ───────────────────────────────────────────────────────────── */
  const handleAufDeleted = useCallback(
    async (deletedId) => {
      try {
        console.log(
          `${LOG_PREFIX} onDelete callback received for ${deletedId} ` +
            "(optimistic hide + refresh).",
        );

        // Optimistically hide whichever slot matches
        setPublishedForm((prev) =>
          prev && prev.id === deletedId ? null : prev,
        );
        setDraftForm((prev) =>
          prev && prev.id === deletedId ? null : prev,
        );
        setArchivedFields([]);

        // Refresh from Firestore to reconcile state (pointer may be cleared)
        await refreshState();
      } catch (e) {
        console.warn(
          `${LOG_PREFIX} onDelete refresh failed (non-fatal):`,
          e,
        );
      }
    },
    [refreshState],
  );

  /* ─────────────────────────────────────────────────────────────
     Archived AUF fields actions (Inspect / Restore)
     - Restore is AUF-only and org-only:
       • requires active AUF (published or draft)
       • requires row.formId matches active AUF id (when present)
       • requires row.organizationId matches current org (when present)
     - Writes a pending restore payload to storage and navigates to the AUF builder.
     - Builder (FormLayoutContext) will apply the restore once and consume the key.
     ───────────────────────────────────────────────────────────── */
  const handleArchivedRowAction = useCallback(
    async (action, row) => {
      try {
        const activeForm = publishedForm || draftForm;

        if (action === "inspect") {
          console.log("[AssetStudio] archived field inspect:", row);
          return;
        }

        if (action !== "restore") return;

        // ─────────────────────────────────────────────────────────────
        // Guardrails: AUF-only + org-only + active AUF required
        // ─────────────────────────────────────────────────────────────
        if (!activeForm?.id) {
          console.warn("[AssetStudio] restore blocked: no active AUF form.");
          return;
        }

        if (!organizationId) {
          console.warn("[AssetStudio] restore blocked: missing organizationId.");
          return;
        }

        if (!row?.ufi || !row?.type) {
          console.warn("[AssetStudio] restore blocked: missing ufi/type on row:", row);
          return;
        }

        // If the archived entry was created for a different AUF, block restore.
        if (row?.formId && row.formId !== activeForm.id) {
          console.warn("[AssetStudio] restore blocked: row.formId mismatch", {
            rowFormId: row.formId,
            activeAufId: activeForm.id,
          });
          return;
        }

        // If you stored organizationId in the archive entry (new guardrail), enforce it.
        if (row?.organizationId && row.organizationId !== organizationId) {
          console.warn("[AssetStudio] restore blocked: row.organizationId mismatch", {
            rowOrg: row.organizationId,
            activeOrg: organizationId,
          });
          return;
        }

        console.log("[AssetStudio] restore requested:", {
          orgId: organizationId,
          aufId: activeForm.id,
          ufi: row.ufi,
          type: row.type,
          optionsSetUFI: row.optionsSetUFI || null,
          wasLibraryFilter: row.wasLibraryFilter === true,
        });

        // Best-effort: if this used to be a Library Filter, re-promote the UFI now
        // so the filter control can reappear quickly after restore.
        if ((canEdit || canPublish) && row.wasLibraryFilter === true) {
          try {
            const inferKind = (t) => {
              if (t === "multi-select") return "array";
              if (t === "date-picker") return "date";
              return "key";
            };
            const kind = inferKind(row.type);

            const orgRef = doc(db, "organizations", organizationId);

            // Keep buckets mutually exclusive (same policy as promoteUfi)
            const payload = {
              "assetPromotionConfig.keys":
                kind === "key" ? arrayUnion(row.ufi) : arrayRemove(row.ufi),
              "assetPromotionConfig.arrayKeys":
                kind === "array" ? arrayUnion(row.ufi) : arrayRemove(row.ufi),
              "assetPromotionConfig.dateKeys":
                kind === "date" ? arrayUnion(row.ufi) : arrayRemove(row.ufi),
            };

            console.log("[AssetStudio] restore: re-promoting archived UFI (best-effort):", {
              ufi: row.ufi,
              kind,
              payload,
            });

            await updateDoc(orgRef, payload);
          } catch (e) {
            console.warn(
              "[AssetStudio] restore: promotion restore failed (non-fatal):",
              e?.message || e,
            );
          }
        }

        // Persist a pending restore request (org-scoped key) for the Builder to apply.
        safeSetItem(AUF_RESTORE_KEY(organizationId), {
          orgId: organizationId,
          aufId: activeForm.id,
          archivedField: row,
          requestedAt: new Date().toISOString(),
        });

        console.log("[AssetStudio] restore: pending payload saved; navigating to builder:", {
          aufId: activeForm.id,
        });

        // Navigate to the AUF builder; FormLayoutContext will apply restore once.
        navigate(`/form-builder/${activeForm.id}/design`, {
          state: { aufRestorePending: true },
        });
      } catch (e) {
        console.warn(
          "[AssetStudio] archived row action failed (non-fatal):",
          e?.message || e,
        );
      }
    },
    [
      publishedForm,
      draftForm,
      organizationId,
      canEdit,
      canPublish,
      navigate,
    ],
  );

  // ─────────────────────────────────────────────────────────────
  // Option A: hide archived rows that have been restored
  // (Builder writes restoredAt/restoredFieldId back onto the form doc)
  // ─────────────────────────────────────────────────────────────
  const visibleArchivedFields = React.useMemo(() => {
    const rows = Array.isArray(archivedFields) ? archivedFields : [];

    // ✅ 2B: Do NOT hide "restored" rows until publish.
    // Keep rows visible if they are marked as restoredPendingPublish.
    // This prevents the UX where the row disappears from the table
    // even though the user still needs to publish to persist the restore.
    const visible = rows.filter((r) => {
      const isRestored = !!r?.restoredAt;
      const pendingPublish = r?.restoredPendingPublish === true;
      // Hide only if it's restored AND not pending publish
      return !(isRestored && !pendingPublish);
    });

    console.log("[AssetStudio] archivedAufFields visibility:", {
      total: rows.length,
      visible: visible.length,
      hiddenRestored: rows.length - visible.length,
    });

    return visible;
  }, [archivedFields]);


  // ─────────────────────────────────────────────────────────────
  // Layout update: match UserProfilePage (Paper wrapper, p:3, header at top)
  // ─────────────────────────────────────────────────────────────
  return (
    <Paper sx={{ width: "100%", p: 3 }}>
      {/* Header placed like in UserProfilePage */}
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
        Asset Studio
      </Typography>

      {/* Keep the same intro text, now inside Paper for consistent margins */}
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        Configure your organization’s single Asset Upload Form. Once a form is
        published, the uploader will use it for all assets in this org. You can
        open the published form in the builder to iterate or create a new draft
        when needed.
      </Typography>

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMsg}
        </Alert>
      )}

      {/* ─────────────────────────────────────────────────────────
          BELOW: Visual area (like Home page)
          • While loading → single skeleton card (no flicker)
          • If PUBLISHED AUF → render HomeCard
          • Else if DRAFT AUF → show “Draft in progress” panel
          • Else → Home-like zero state (tuned sizing)
         ───────────────────────────────────────────────────────── */}
      <Box sx={{ mt: 1 }}>
        {loading ? (
          // ✅ Single skeleton sized/place as the HomeCard
          <Grid container spacing={2}>
            <Grid item sx={{ minWidth: 150, width: 150 }}>
              <Box sx={{ width: 150 }}>
                <Skeleton
                  variant="rectangular"
                  width={150}
                  height={195}
                  sx={{ borderRadius: "8px" }}
                />
                <Skeleton variant="text" width={140} sx={{ mt: 1 }} />
                <Skeleton variant="text" width={100} />
              </Box>
            </Grid>
          </Grid>
        ) : publishedForm ? (
          // Provide FormContext locally so HomeCard → useDuplicateForm works
          <FormProvider>
            <Grid container spacing={2}>
              <HomeCard
                form={{
                  id: publishedForm.id,
                  status: publishedForm.status || "published",
                  title:
                    publishedForm.title ||
                    publishedForm.formDetails?.title ||
                    "Asset Upload Form",
                  description:
                    publishedForm.description ||
                    publishedForm.formDetails?.description ||
                    "Active upload form for your organization.",
                  screenshotPath: publishedForm.screenshotPath || null,
                  screenshotThumbnail:
                    publishedForm.screenshotThumbnail || null,
                  screenshotUrl: publishedForm.screenshotUrl || null,
                  isTemplate: publishedForm.isTemplate || false,

                  isAssetUploadForm:
                    publishedForm.isAssetUploadForm ?? true,
                  assetIngestConfig:
                    publishedForm.assetIngestConfig ?? null,
                  formType: publishedForm.formType ?? undefined,

                  owner:
                    publishedForm.owner ||
                    publishedForm.createdBy ||
                    null,
                  sharedWith: publishedForm.sharedWith || {},
                  organizationId:
                    publishedForm.organizationId || organizationId,
                  orgAccess: publishedForm.orgAccess || "view",
                  forceFit: publishedForm.forceFit,
                  isPublic: publishedForm.isPublic,
                  vanitySlug: publishedForm.vanitySlug,
                }}
                onDelete={handleAufDeleted}
              />
            </Grid>
          </FormProvider>
        ) : draftForm ? (
          // ✅ Draft exists (no published). Don’t show “no form” zero state—nudge to continue editing.
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "24vh",
              width: "100%",
              textAlign: "center",
              backgroundColor: "#fafafa",
              border: "1px dashed #e0e0e0",
              borderRadius: "8px",
              p: 3,
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#424242" }}
            >
              Draft Asset Upload Form in progress
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#616161", mb: 2 }}
            >
              You have a draft started. Continue editing and publish when
              ready.
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={() => {
                  console.log(
                    `${LOG_PREFIX} Opening DRAFT form in builder:`,
                    draftForm.id,
                  );
                  navigate(`/form-builder/${draftForm.id}/design`);
                }}
                disabled={!canEdit}
              >
                Edit Draft
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  console.log(
                    `${LOG_PREFIX} Draft → navigate to publish flow (builder):`,
                    draftForm.id,
                  );
                  navigate(`/form-builder/${draftForm.id}/design`);
                }}
                disabled={!canPublish}
              >
                Publish
              </Button>
            </Stack>
          </Box>
        ) : (
          // ✅ Home-like zero state (no AUF at all) — tuned sizing to avoid overflow
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "40vh", // ⬆ more vertical room to avoid spill
              width: "100%",
              textAlign: "center",
              backgroundColor: "#f9f9f9",
              borderRadius: "8px",
              p: 4, // ⬆ a bit more padding for comfort
              mt: 1,
            }}
          >
            <DescriptionIcon
              sx={{
                fontSize: 32,
                color: "#9e9e9e",
                mb: 1.25,
              }}
            />
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: "#616161",
                fontSize: "1rem",
                fontWeight: 600,
              }}
            >
              No Asset Upload Form
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#757575", mb: 2 }}
            >
              Create your organization’s Asset Upload Form to begin managing
              assets.
            </Typography>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={!canCreate || busy}
            >
              {busy ? "Working…" : "Create Upload Form"}
            </Button>
          </Box>
        )}
      </Box>

      {/* Archived AUF fields table – appears under the card/zero-state
          and renders only when there are archived entries */}
      <ArchivedAufFieldsTable
        archivedFields={visibleArchivedFields}
        onRowAction={handleArchivedRowAction}
      />
    </Paper>
  );
};

export default AssetStudioSettingsPage;
